import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import alertSystem from './alertSystem';

export enum AuditAction {
  // User actions
  USER_LOGIN = 'user_login',
  USER_LOGOUT = 'user_logout',
  USER_CREATED = 'user_created',
  USER_UPDATED = 'user_updated',
  USER_DELETED = 'user_deleted',
  
  // Member actions
  MEMBER_CREATED = 'member_created',
  MEMBER_UPDATED = 'member_updated',
  MEMBER_DELETED = 'member_deleted',
  MEMBER_PROMOTED_TO_ADMIN = 'member_promoted_to_admin',
  MEMBER_DEMOTED_FROM_ADMIN = 'member_demoted_from_admin',
  
  // Family actions
  FAMILY_CREATED = 'family_created',
  FAMILY_UPDATED = 'family_updated',
  FAMILY_DELETED = 'family_deleted',
  FAMILY_OWNERSHIP_TRANSFERRED = 'family_ownership_transferred',
  
  // Subscription actions
  SUBSCRIPTION_CREATED = 'subscription_created',
  SUBSCRIPTION_UPDATED = 'subscription_updated',
  SUBSCRIPTION_CANCELED = 'subscription_canceled',
  PLAN_CHANGED = 'plan_changed',
  
  // Activity actions
  ACTIVITY_CREATED = 'activity_created',
  ACTIVITY_UPDATED = 'activity_updated',
  ACTIVITY_DELETED = 'activity_deleted',
  ACTIVITY_COMPLETED = 'activity_completed',
  
  // Checklist actions
  CHECKLIST_CREATED = 'checklist_created',
  CHECKLIST_UPDATED = 'checklist_updated',
  CHECKLIST_DELETED = 'checklist_deleted',
  CHECKLIST_COMPLETED = 'checklist_completed',
  
  // Admin actions
  ADMIN_SETTINGS_CHANGED = 'admin_settings_changed',
  PLAN_LIMITS_CHANGED = 'plan_limits_changed',
  BETA_CODE_CREATED = 'beta_code_created',
  
  // Security events
  RATE_LIMIT_EXCEEDED = 'rate_limit_exceeded',
  UNAUTHORIZED_ACCESS_ATTEMPT = 'unauthorized_access_attempt',
  SESSION_EXPIRED = 'session_expired',
}

export enum AuditSeverity {
  INFO = 'info',
  WARNING = 'warning',
  ERROR = 'error',
  CRITICAL = 'critical',
}

interface AuditLogEntry {
  action: AuditAction;
  userId: string;
  userEmail?: string;
  severity: AuditSeverity;
  resourceType?: string; // 'family', 'member', 'activity', etc.
  resourceId?: string;
  familyId?: string;
  details?: Record<string, any>;
  ipAddress?: string;
  userAgent?: string;
  timestamp: any; // serverTimestamp
}

class AuditLogger {
  /**
   * Registra una acción en el log de auditoría
   */
  async log(
    action: AuditAction,
    userId: string,
    options: {
      userEmail?: string;
      severity?: AuditSeverity;
      resourceType?: string;
      resourceId?: string;
      familyId?: string;
      details?: Record<string, any>;
    } = {}
  ): Promise<void> {
    try {
      const entry: AuditLogEntry = {
        action,
        userId,
        userEmail: options.userEmail,
        severity: options.severity || this.getSeverityForAction(action),
        resourceType: options.resourceType,
        resourceId: options.resourceId,
        familyId: options.familyId,
        details: options.details,
        userAgent: navigator.userAgent,
        timestamp: serverTimestamp(),
      };

      // Remover campos undefined (Firestore no los permite)
      const cleanEntry = Object.fromEntries(
        Object.entries(entry).filter(([_, value]) => value !== undefined)
      ) as AuditLogEntry;

      await addDoc(collection(db, 'auditLogs'), cleanEntry);
      
      // Log críticos también en console
      if (entry.severity === AuditSeverity.CRITICAL || entry.severity === AuditSeverity.ERROR) {
      }

      // Crear alerta automática si es necesario
      await alertSystem.checkAndCreateAlert(action, entry.severity, userId, {
        userEmail: options.userEmail,
        resourceType: options.resourceType,
        resourceId: options.resourceId,
        familyId: options.familyId,
        details: options.details,
      });
    } catch (error: any) {
      // No fallar la operación principal si el log falla
      // DEBUG: Mostrar temporalmente errores en consola para identificar el toast
      if (error?.code === 'permission-denied') {
        console.warn('[AuditLogger] Permission denied escribiendo audit log:', action);
      } else if (error) {
        console.warn('[AuditLogger] Error escribiendo audit log:', error?.code || error?.message);
      }
    }
  }

  /**
   * Determina la severidad por defecto según la acción
   */
  private getSeverityForAction(action: AuditAction): AuditSeverity {
    // Acciones críticas
    if ([
      AuditAction.USER_DELETED,
      AuditAction.FAMILY_DELETED,
      AuditAction.FAMILY_OWNERSHIP_TRANSFERRED,
      AuditAction.SUBSCRIPTION_CANCELED,
      AuditAction.UNAUTHORIZED_ACCESS_ATTEMPT,
    ].includes(action)) {
      return AuditSeverity.CRITICAL;
    }

    // Acciones de advertencia
    if ([
      AuditAction.MEMBER_PROMOTED_TO_ADMIN,
      AuditAction.MEMBER_DEMOTED_FROM_ADMIN,
      AuditAction.PLAN_CHANGED,
      AuditAction.ADMIN_SETTINGS_CHANGED,
      AuditAction.PLAN_LIMITS_CHANGED,
      AuditAction.RATE_LIMIT_EXCEEDED,
      AuditAction.SESSION_EXPIRED,
    ].includes(action)) {
      return AuditSeverity.WARNING;
    }

    // Por defecto: info
    return AuditSeverity.INFO;
  }

  /**
   * Logs específicos para acciones comunes
   */
  
  logMemberCreated(userId: string, memberId: string, familyId: string, memberName: string) {
    return this.log(AuditAction.MEMBER_CREATED, userId, {
      resourceType: 'member',
      resourceId: memberId,
      familyId,
      details: { memberName },
    });
  }

  logMemberPromoted(userId: string, memberId: string, familyId: string, memberName: string) {
    return this.log(AuditAction.MEMBER_PROMOTED_TO_ADMIN, userId, {
      resourceType: 'member',
      resourceId: memberId,
      familyId,
      details: { memberName },
      severity: AuditSeverity.WARNING,
    });
  }

  logMemberDemoted(userId: string, memberId: string, familyId: string, memberName: string) {
    return this.log(AuditAction.MEMBER_DEMOTED_FROM_ADMIN, userId, {
      resourceType: 'member',
      resourceId: memberId,
      familyId,
      details: { memberName },
      severity: AuditSeverity.WARNING,
    });
  }

  logFamilyCreated(userId: string, familyId: string, familyName: string) {
    return this.log(AuditAction.FAMILY_CREATED, userId, {
      resourceType: 'family',
      resourceId: familyId,
      familyId,
      details: { familyName },
    });
  }

  logPlanChanged(userId: string, familyId: string, oldPlan: string, newPlan: string) {
    return this.log(AuditAction.PLAN_CHANGED, userId, {
      resourceType: 'subscription',
      familyId,
      details: { oldPlan, newPlan },
      severity: AuditSeverity.WARNING,
    });
  }

  logRateLimitExceeded(userId: string, operation: string) {
    return this.log(AuditAction.RATE_LIMIT_EXCEEDED, userId, {
      details: { operation },
      severity: AuditSeverity.WARNING,
    });
  }

  logUnauthorizedAccess(userId: string, attemptedResource: string, attemptedAction: string) {
    return this.log(AuditAction.UNAUTHORIZED_ACCESS_ATTEMPT, userId, {
      details: { attemptedResource, attemptedAction },
      severity: AuditSeverity.CRITICAL,
    });
  }

  logAdminSettingsChanged(userId: string, changedSettings: Record<string, any>) {
    return this.log(AuditAction.ADMIN_SETTINGS_CHANGED, userId, {
      details: { changedSettings },
      severity: AuditSeverity.WARNING,
    });
  }

  logPlanLimitsChanged(userId: string, planId: string, oldLimits: any, newLimits: any) {
    return this.log(AuditAction.PLAN_LIMITS_CHANGED, userId, {
      resourceType: 'plan',
      resourceId: planId,
      details: { oldLimits, newLimits },
      severity: AuditSeverity.WARNING,
    });
  }

  // Logs adicionales para actividades
  logActivityCreated(userId: string, activityId: string, familyId: string, activityName: string) {
    return this.log(AuditAction.ACTIVITY_CREATED, userId, {
      resourceType: 'activity',
      resourceId: activityId,
      familyId,
      details: { activityName },
    });
  }

  logActivityDeleted(userId: string, activityId: string, familyId: string, activityName: string) {
    return this.log(AuditAction.ACTIVITY_DELETED, userId, {
      resourceType: 'activity',
      resourceId: activityId,
      familyId,
      details: { activityName },
      severity: AuditSeverity.WARNING,
    });
  }

  // Logs adicionales para checklists
  logChecklistCreated(userId: string, checklistId: string, familyId: string, checklistName: string) {
    return this.log(AuditAction.CHECKLIST_CREATED, userId, {
      resourceType: 'checklist',
      resourceId: checklistId,
      familyId,
      details: { checklistName },
    });
  }

  logChecklistDeleted(userId: string, checklistId: string, familyId: string, checklistName: string) {
    return this.log(AuditAction.CHECKLIST_DELETED, userId, {
      resourceType: 'checklist',
      resourceId: checklistId,
      familyId,
      details: { checklistName },
      severity: AuditSeverity.WARNING,
    });
  }

  // Logs de autenticación
  logUserLogin(userId: string, userEmail?: string) {
    return this.log(AuditAction.USER_LOGIN, userId, {
      userEmail,
      severity: AuditSeverity.INFO,
    });
  }

  logUserLogout(userId: string, userEmail?: string) {
    return this.log(AuditAction.USER_LOGOUT, userId, {
      userEmail,
      severity: AuditSeverity.INFO,
    });
  }

  logSessionExpired(userId: string, reason: 'timeout' | 'inactivity') {
    return this.log(AuditAction.SESSION_EXPIRED, userId, {
      details: { reason },
      severity: AuditSeverity.WARNING,
    });
  }

  // Logs de familia
  logFamilyDeleted(userId: string, familyId: string, familyName: string) {
    return this.log(AuditAction.FAMILY_DELETED, userId, {
      resourceType: 'family',
      resourceId: familyId,
      familyId,
      details: { familyName },
      severity: AuditSeverity.CRITICAL,
    });
  }

  logMemberDeleted(userId: string, memberId: string, familyId: string, memberName: string) {
    return this.log(AuditAction.MEMBER_DELETED, userId, {
      resourceType: 'member',
      resourceId: memberId,
      familyId,
      details: { memberName },
      severity: AuditSeverity.WARNING,
    });
  }

  logMemberUpdated(userId: string, memberId: string, familyId: string, changes: any) {
    return this.log(AuditAction.MEMBER_UPDATED, userId, {
      resourceType: 'member',
      resourceId: memberId,
      familyId,
      details: { changes },
    });
  }
}

// Instancia singleton
const auditLogger = new AuditLogger();

export default auditLogger;
